/**
 * ArchLang v0.10 — governance/query declaration parser (§1.9, §4).
 *
 * A thin layer over `SelectorExprParser`: `query`/`policy` declarations embed
 * selectors and expressions, so this subclass reuses the exact same token
 * cursor, glued-token detection, and `parseSelector`/`parseExpression`/getter
 * productions. It parses the NEW declaration heads; the main file parser
 * (parser.ts) dispatches to it at a `policy`/`query` member head and reads `pos`
 * back. The selector slots terminate naturally — the member keywords
 * (`forbid`/`require`/`except`/`escalate`/`when`) are not selector
 * continuations, so no explicit logical-line scan is needed here.
 *
 * Only `query` + `policy` land in this slice (S8-core); `style bundle`, `lens`,
 * and `use policy` follow, alongside the view-v2 replacement + consumer sweep.
 */

import type { FieldDecl, SourceSpan, StableId, Value } from "./types.js";
import { stableId } from "./types.js";
import { tokenize, RESERVED_KEYWORDS, type Token } from "./lexer.js";
import { SelectorExprParser, type SelectorParseError } from "./selector-parser.js";
import { renderDiagnostic } from "./diagnostics/catalog.js";
import type { DiagnosticCode } from "./diagnostics/codes.js";
import type { ExprGetter, Selector } from "./selector-ast.js";
import type {
  ChangeVerb,
  EscalateRule,
  ExceptRule,
  FlowBodyClause,
  FlowRef,
  ForbidRule,
  KnobBinding,
  KnobDecl,
  LensDecl,
  LensEntry,
  MatrixOrder,
  PolicyDecl,
  PolicyRule,
  QueryDecl,
  RequireRule,
  ReviewFrom,
  ReviewReq,
  StoryChapter,
  StyleBundleDecl,
  StyleMember,
  TableColumn,
  TestAssertion,
  TestCompareOp,
  TestDecl,
  TestPin,
  TestPinValue,
  TestRef,
  TestRefSegment,
  UsePolicyStmt,
  ViewClause,
  ViewDeclV2,
  WhenGate,
  ScrubPoint,
} from "./v010-ast.js";

const CHANGE_VERBS: ReadonlySet<string> = new Set(["added", "removed", "changed"]);

/** Representation clause kinds — a view may have at most one (§3; board = none). */
const VIEW_REPRESENTATIONS: ReadonlySet<string> = new Set(["table", "matrix", "grid", "index", "flow"]);

/**
 * Chapters a story may carry. A hard cap, not a guideline: a story is a walk a
 * reader holds in their head, and the whole claim of the feature is that the
 * authored part stays small enough to be worth reviewing.
 */
const STORY_CHAPTER_CAP = 5;

/**
 * A `SelectorParseError` enriched with the diagnostic-catalog fields so a
 * declaration-parser error renders per-locale like a semantic diagnostic. The
 * base `errors` array is typed `SelectorParseError[]`; the catalog fields ride
 * as optional extras (parser.ts spreads `{ ...e }`, so they propagate to
 * `ParseError` at runtime). `code` is always "PARSE_ERROR".
 */
interface EnrichedParseError extends SelectorParseError {
  readonly code?: DiagnosticCode;
  readonly msgKey?: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

export class V010DeclParser extends SelectorExprParser {
  // ===========================================================================
  // query
  // ===========================================================================

  /** `query [#id] <Name>: <selector>`. Current token is the `query` head. */
  parseQuery(exported = false): QueryDecl {
    const start = this.advance(); // `query`
    const idTok = this.matchToken("STABLE_ID");
    const nameTok = this.consume("IDENTIFIER", "Expected a query name");
    this.consume("COLON", "Expected ':' after the query name");
    const selector = this.parseSelector();
    return {
      ...(idTok ? { id: stableId(idTok.value.slice(1)) as StableId, idSource: idTok.span } : {}),
      name: nameTok.value,
      nameSource: nameTok.span,
      selector,
      ...(exported ? { exported: true } : {}),
      source: this.spanFrom(start),
    };
  }

  // ===========================================================================
  // policy
  // ===========================================================================

  /** `policy [#id] <Name> { <header> <rules> }`. Current token is the `policy` head. */
  parsePolicy(exported = false): PolicyDecl {
    const start = this.advance(); // `policy`
    const idTok = this.matchToken("STABLE_ID");
    const nameTok = this.consume("IDENTIFIER", "Expected a policy name");
    this.consume("LBRACE", "Expected '{' after the policy name");

    const fields: FieldDecl[] = [];
    const rules: PolicyRule[] = [];
    let description: string | undefined;
    let sawRule = false;

    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;

      // Header zone: bare description string, or a `path: value` field.
      if (!sawRule && this.check("STRING")) {
        const s = this.advance();
        description = description === undefined ? s.value : `${description}\n${s.value}`;
        continue;
      }
      if (this.isFieldHead()) {
        if (sawRule) {
          this.errK(this.peek(), "parse.policyHeaderAfterRule");
        }
        fields.push(this.parseField());
        continue;
      }

      // Rules / gates.
      const rule = this.parsePolicyRule();
      if (rule) {
        sawRule = true;
        rules.push(rule);
        continue;
      }
      // Unrecognised member — recover.
      this.errK(this.peek(), "parse.unexpectedInPolicyBody", { tok: this.peek().value || this.peek().type });
      this.advance();
    }

    this.consume("RBRACE", "Expected '}' to close the policy body");
    return {
      ...(idTok ? { id: stableId(idTok.value.slice(1)) as StableId, idSource: idTok.span } : {}),
      name: nameTok.value,
      nameSource: nameTok.span,
      description,
      fields,
      rules,
      ...(exported ? { exported: true } : {}),
      source: this.spanFrom(start),
    };
  }

  private parsePolicyRule(): PolicyRule | null {
    if (this.isKw("forbid")) return this.parseForbid();
    if (this.isKw("require")) return this.parseRequire();
    if (this.isKw("except")) return this.parseExcept();
    if (this.isKw("escalate")) return this.parseEscalate();
    if (this.isKw("when")) return this.parseWhen();
    return null;
  }

  private parseForbid(): ForbidRule {
    const start = this.advance(); // `forbid`
    const selector = this.parseSelector();
    return { kind: "forbid", selector, source: this.spanFrom(start) };
  }

  private parseRequire(): RequireRule {
    const start = this.advance(); // `require`
    const subject = this.parseSelector();
    this.consume("COLON", "A `require` obligation is separated from its subject by ':'");
    const obligation = this.parseSelector();
    return { kind: "require", subject, obligation, source: this.spanFrom(start) };
  }

  private parseExcept(): ExceptRule {
    const start = this.advance(); // `except`
    const selector = this.parseSelector();
    let reason = "";
    let reasonSource: SourceSpan | undefined;
    if (this.check("STRING")) {
      const s = this.advance();
      reason = s.value;
      reasonSource = s.span;
    } else {
      this.errK(this.peek(), "parse.exceptNeedsReason");
    }
    const fields: FieldDecl[] = [];
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (this.isFieldHead()) {
          fields.push(this.parseField());
          continue;
        }
        this.errK(this.peek(), "parse.exceptFieldBodyOnly");
        this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close the `except` field body");
    }
    return { kind: "except", selector, reason, reasonSource, fields, source: this.spanFrom(start) };
  }

  private parseEscalate(): EscalateRule {
    const start = this.advance(); // `escalate`
    const target = this.parseQualifiedName();
    this.consumeKw("to", "Expected `to` in `escalate <target> to <severity>`");
    const sevTok = this.consume("IDENTIFIER", "Expected a severity after `to`");
    return { kind: "escalate", target, severity: sevTok.value, source: this.spanFrom(start) };
  }

  private parseWhen(): WhenGate {
    const start = this.advance(); // `when`
    const subject = this.parseSelector();
    // The token after the subject must be a change verb (the greedy rule).
    const verbTok = this.peek();
    if (!(verbTok.type === "IDENTIFIER" && CHANGE_VERBS.has(verbTok.value))) {
      this.errK(verbTok, "parse.expectedChangeVerb");
      return { kind: "when", subject, verb: "changed", body: [], source: this.spanFrom(start) };
    }
    this.advance();
    const verb = verbTok.value as ChangeVerb;
    // `changed(<getter>)` resolved-value portal — spacing-insensitive.
    let changedGetter: ExprGetter | undefined;
    if (verb === "changed" && this.check("LPAREN")) {
      this.advance();
      changedGetter = this.parseChangedGetter();
      this.consume("RPAREN", "Expected ')' to close `changed(...)`");
    }
    this.consume("LBRACE", "Expected '{' to open the gate body");
    const body: ReviewReq[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const req = this.parseReviewReq();
      if (req) body.push(req);
      else {
        this.errK(this.peek(), "parse.gateBodyReviewOnly");
        this.advance();
      }
    }
    this.consume("RBRACE", "Expected '}' to close the gate body");
    return {
      kind: "when",
      subject,
      verb,
      ...(changedGetter ? { changedGetter } : {}),
      body,
      source: this.spanFrom(start),
    };
  }

  /** `require review from [N of] <getter | key>`. */
  private parseReviewReq(): ReviewReq | null {
    if (!this.isKw("require")) return null;
    const start = this.advance(); // `require`
    this.consumeKw("review", "Expected `review` after `require` in a gate body");
    // `from` is a lexer keyword (FROM token), not a contextual identifier.
    this.consume("FROM", "Expected `from` after `require review`");
    let quorum = 1;
    if (this.check("NUMBER") && this.peekN(1)?.type === "IDENTIFIER" && this.peekN(1)?.value === "of") {
      quorum = Number(this.advance().value);
      this.advance(); // `of`
    }
    let from: ReviewFrom;
    if (this.check("AT") || this.check("ATAT")) {
      from = { kind: "getter", getter: this.parseGetter() };
    } else {
      const keyTok = this.consume("IDENTIFIER", "Expected a reviewer getter or key after `from`");
      from = { kind: "key", name: keyTok.value };
    }
    return { kind: "review", quorum, from, source: this.spanFrom(start) };
  }

  /** The getter inside a `changed(<getter>)` portal — field/aspect/builtin. */
  private parseChangedGetter(): ExprGetter {
    if (!(this.check("AT") || this.check("ATAT"))) {
      this.errK(this.peek(), "parse.changedGetterSigiled");
    }
    return this.parseGetter();
  }

  // ===========================================================================
  // test (process-tests P0 — parse only, INERT until the evaluator)
  // ===========================================================================

  /**
   * `test [#id] "<name>" [of <Process>] { <header> when(:|{}) then(:|{}) }`.
   * Current token is the `test` head (a contextual identifier). Mirrors
   * `parsePolicy`'s header-zone scaffold; the `when`/`then` block bodies are the
   * two net-new sub-grammars (pins + assertions).
   */
  parseTest(): TestDecl {
    const start = this.advance(); // `test`
    const idTok = this.matchToken("STABLE_ID");
    const nameTok = this.consume("STRING", "Expected a test name string after `test`");

    let target: TestDecl["target"];
    let targetSource: SourceSpan | undefined;
    if (this.isKw("of")) {
      this.advance(); // `of`
      const tstart = this.peek();
      target = this.parseQualifiedName();
      targetSource = this.spanFrom(tstart);
    } else {
      // Rev-1 scope: a test targets exactly one process (design §4).
      this.errK(nameTok, "parse.testMissingTarget");
    }

    this.consume("LBRACE", "Expected '{' after the test header");

    const fields: FieldDecl[] = [];
    const pins: TestPin[] = [];
    const assertions: TestAssertion[] = [];
    let description: string | undefined;
    let whenSource: SourceSpan | undefined;
    let thenSource: SourceSpan | undefined;
    let sawBody = false;

    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;

      // `when` / `then` bodies. Checked BEFORE the field branch: `when:` (the
      // one-liner form) has `when` followed by `:`, which `isFieldHead` would
      // otherwise mistake for a field named `when`.
      if (this.isKw("when")) {
        const wstart = this.advance();
        this.parseTestBlock(() => pins.push(this.parsePin()));
        whenSource = this.spanFrom(wstart);
        sawBody = true;
        continue;
      }
      if (this.isKw("then")) {
        const tstart = this.advance();
        this.parseThenBody(assertions);
        thenSource = this.spanFrom(tstart);
        sawBody = true;
        continue;
      }

      // Header zone: a bare description string.
      if (this.check("STRING")) {
        const s = this.advance();
        if (sawBody) {
          this.errK(s, "parse.testDescAfterBody");
          continue;
        }
        description = description === undefined ? s.value : `${description}\n${s.value}`;
        continue;
      }
      // Header zone: a `key: value` field.
      if (this.isFieldHead()) {
        if (sawBody) this.errK(this.peek(), "parse.testFieldAfterBody");
        fields.push(this.parseField());
        continue;
      }

      this.errK(this.peek(), "parse.unexpectedInTestBody", { tok: this.peek().value || this.peek().type });
      this.advance();
    }

    this.consume("RBRACE", "Expected '}' to close the test body");

    return {
      ...(idTok ? { id: stableId(idTok.value.slice(1)) as StableId, idSource: idTok.span } : {}),
      name: nameTok.value,
      nameSource: nameTok.span,
      ...(target ? { target, targetSource } : {}),
      ...(description !== undefined ? { description } : {}),
      fields,
      pins,
      assertions,
      ...(whenSource ? { whenSource } : {}),
      ...(thenSource ? { thenSource } : {}),
      source: this.spanFrom(start),
    };
  }

  /**
   * The universal `:`-one-liner / `{}`-multiline block rule (like `try` /
   * `parallel`). `{` → parse members until `}`; `:` → parse exactly one member.
   */
  private parseTestBlock(member: () => void): void {
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        const before = this.pos;
        member();
        // Recovery: a malformed member that consumed nothing would spin this
        // loop forever — force progress past the offending token.
        if (this.pos === before) this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close the block");
      return;
    }
    this.consume("COLON", "Expected ':' or '{' after `when`/`then`");
    member();
  }

  /**
   * A `then` body — supports the `then parallel { … }` order-free group (design
   * §7) alongside the `:`/`{}` rule; a bare `then parallel {` has no braces of
   * its own, so it falls through to `parseAssertion` (which owns the group).
   */
  private parseThenBody(out: TestAssertion[]): void {
    if (this.check("LBRACE") || this.check("COLON")) {
      this.parseTestBlock(() => out.push(this.parseAssertion()));
      return;
    }
    out.push(this.parseAssertion());
  }

  /** `<key>: <value>` — one `when` pin. */
  private parsePin(): TestPin {
    const start = this.peek();
    const key = this.parseTestRef();
    this.consume("COLON", "Expected ':' after a pin key");
    const value = this.parsePinValue();
    return { key, value, source: this.spanFrom(start) };
  }

  /** A pin value, incl. the comma-sequence (per-attempt / go-loop) form. */
  private parsePinValue(): TestPinValue {
    // Ordered `select` list `[a, b]` (never sequenced).
    if (this.check("LBRACKET")) {
      this.advance();
      const items: string[] = [];
      while (!this.check("RBRACKET") && !this.isAtEnd()) {
        if (this.check("STRING") || this.check("IDENTIFIER")) items.push(this.advance().value);
        else break;
        if (!this.check("COMMA")) break;
        this.advance();
      }
      this.consume("RBRACKET", "Expected ']' to close a `select` list");
      return { kind: "list", items };
    }
    const first = this.parsePinAtom();
    if (!this.check("COMMA")) return first;
    const steps: TestPinValue[] = [first];
    while (this.check("COMMA")) {
      this.advance();
      steps.push(this.parsePinAtom());
    }
    return { kind: "sequence", steps };
  }

  /** One pin atom (an element of a comma sequence). */
  private parsePinAtom(): TestPinValue {
    if (this.isKw("fails")) {
      this.advance();
      const cause = this.check("STRING") ? this.advance().value : undefined;
      return cause !== undefined ? { kind: "fail", cause } : { kind: "fail" };
    }
    if (this.isKw("after")) {
      this.advance();
      return { kind: "after", duration: this.parseDurationText() };
    }
    if (this.isKw("takes")) {
      this.advance();
      return { kind: "takes", duration: this.parseDurationText() };
    }
    if (this.check("STRING")) return { kind: "cause", value: this.advance().value };
    if (this.check("NUMBER")) return { kind: "count", n: Number(this.advance().value) };
    if (this.check("IDENTIFIER")) return { kind: "answer", word: this.advance().value };
    this.errK(this.peek(), "parse.expectedPinValue");
    return { kind: "answer", word: "" };
  }

  /** One `then` assertion — occurrence / terminator / time budget / parallel group. */
  private parseAssertion(): TestAssertion {
    const start = this.peek();
    // Terminator (`fail`/`finish` are lexer keywords, reused from the process
    // grammar). `fail all` / `finish all`, else an optional `"<label>"`.
    if (this.check("FAIL") || this.check("FINISH")) {
      const which = this.advance().type === "FAIL" ? "fail" : "finish";
      if (this.isKw("all")) {
        this.advance();
        return { kind: "terminator", which, all: true, source: this.spanFrom(start) };
      }
      const label = this.check("STRING") ? this.advance().value : undefined;
      return { kind: "terminator", which, ...(label !== undefined ? { label } : {}), source: this.spanFrom(start) };
    }
    // Time budget: `time (system|business) <op> <dur>`.
    if (this.isKw("time")) {
      this.advance();
      const axis = this.consume("IDENTIFIER", "Expected `system` or `business` after `time`").value;
      const op = this.parseCompareOp() ?? "=";
      const duration = this.parseDurationText();
      return { kind: "time", axis, op, duration, source: this.spanFrom(start) };
    }
    // Order-free group: `parallel { … }`.
    if (this.check("PARALLEL")) {
      this.advance();
      const group: TestAssertion[] = [];
      this.consume("LBRACE", "Expected '{' after `parallel`");
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        const before = this.pos;
        group.push(this.parseAssertion());
        if (this.pos === before) this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close a `parallel` group");
      return { kind: "parallelGroup", assertions: group, source: this.spanFrom(start) };
    }
    // Occurrence: `<ref> [happens|never|sometimes] [count]`.
    const ref = this.parseTestRef();
    let modal: "happens" | "never" | "sometimes" | undefined;
    let count: { op: TestCompareOp; n: number } | undefined;
    if (this.isKw("happens")) {
      this.advance();
      modal = "happens";
      count = this.parseCountTail();
    } else if (this.isKw("never")) {
      this.advance();
      modal = "never";
    } else if (this.isKw("sometimes")) {
      this.advance();
      modal = "sometimes";
    }
    // A trailing string in the assertion slot is a process call-comment leaking
    // in — forbidden here (design §5a).
    if (this.check("STRING")) {
      this.errK(this.peek(), "parse.assertionTrailingString");
    }
    return { kind: "occurrence", ref, ...(modal ? { modal } : {}), ...(count ? { count } : {}), source: this.spanFrom(start) };
  }

  /** `[<op>] N` count tail after `happens` — a bare `N` is exact (`=`). */
  private parseCountTail(): { op: TestCompareOp; n: number } | undefined {
    const op = this.parseCompareOp();
    if (this.check("NUMBER")) return { op: op ?? "=", n: Number(this.advance().value) };
    if (op !== undefined) this.errK(this.peek(), "parse.expectedCountNumber");
    return undefined;
  }

  /** A comparison operator token → the AST op, or undefined if none is next. */
  private parseCompareOp(): TestCompareOp | undefined {
    switch (this.peek().type) {
      case "GE": this.advance(); return ">=";
      case "LE": this.advance(); return "<=";
      case "LT": this.advance(); return "<";
      case "GT": this.advance(); return ">";
      case "EQUALS": this.advance(); return "=";
      default: return undefined;
    }
  }

  /**
   * A dotted ref shared by pins and assertions: names and/or condition strings,
   * with an optional leading `Caller >` (inbound-arrival pins).
   */
  private parseTestRef(): TestRef {
    const start = this.peek();
    const first = this.parseRefSegments();
    if (this.check("GT")) {
      // What we parsed is the caller (names only); the callee follows `>`.
      this.advance();
      const segments = this.parseRefSegments();
      const caller = first.map((s) => s.value);
      return { caller, segments, source: this.spanFrom(start) };
    }
    return { segments: first, source: this.spanFrom(start) };
  }

  private parseRefSegments(): TestRefSegment[] {
    const segs: TestRefSegment[] = [this.parseRefSegment()];
    while (this.check("DOT")) {
      this.advance();
      segs.push(this.parseRefSegment());
    }
    return segs;
  }

  private parseRefSegment(): TestRefSegment {
    if (this.check("STRING")) return { kind: "string", value: this.advance().value };
    if (this.check("IDENTIFIER")) return { kind: "name", value: this.advance().value };
    // v0.8.2 keyword-names (SPEC §4.9): a reserved word in a NAME position — a
    // choice-key / branch-ref segment — is a name (`override: done`, matching a
    // `branch override`). The bare form is the contract; the quoted form also works.
    if (RESERVED_KEYWORDS.has(this.peek().value)) return { kind: "name", value: this.advance().value };
    return { kind: "name", value: this.consume("IDENTIFIER", "Expected a reference segment").value };
  }

  /** Capture a compact duration (`3d`, `500ms`) as text — `NUMBER` + unit `IDENTIFIER`. */
  private parseDurationText(): string {
    let text = "";
    if (this.check("NUMBER")) text += this.advance().value;
    if (this.check("IDENTIFIER")) text += this.advance().value;
    if (text === "") this.errK(this.peek(), "parse.expectedDuration");
    return text;
  }

  // ===========================================================================
  // style bundle
  // ===========================================================================

  /** `style bundle [#id] <Name> { <style body> }`. Current token is `style`. */
  parseStyleBundle(): StyleBundleDecl {
    const start = this.advance(); // `style`
    this.consumeKw("bundle", "Expected `bundle` after `style`");
    const idTok = this.matchToken("STABLE_ID");
    const nameTok = this.consume("IDENTIFIER", "Expected a bundle name");
    const members = this.parseStyleBody();
    return {
      ...(idTok ? { id: stableId(idTok.value.slice(1)) as StableId, idSource: idTok.span } : {}),
      name: nameTok.value,
      nameSource: nameTok.span,
      members,
      source: this.spanFrom(start),
    };
  }

  /** `{ <field rewrites | field groups | directives | use Bundle> }`. */
  private parseStyleBody(): StyleMember[] {
    this.consume("LBRACE", "Expected '{' to open a style body");
    const members: StyleMember[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const m = this.parseStyleMember();
      if (m) members.push(m);
      else {
        this.errK(this.peek(), "parse.unexpectedInStyleBody", { tok: this.peek().value || this.peek().type });
        this.advance();
      }
    }
    this.consume("RBRACE", "Expected '}' to close a style body");
    return members;
  }

  private parseStyleMember(): StyleMember | null {
    const tok = this.peek();
    // `use <Bundle>` — `use` is a lexer keyword (USE token). `use:`/`use{` is a
    // field literally named `use`; `use <ident>` is the directive.
    if (this.check("USE") && this.peekN(1)?.type === "IDENTIFIER") {
      this.advance();
      const bundle = this.advance().value;
      return { kind: "use", bundle, source: this.spanFrom(tok) };
    }
    // Contextual directives: head + `:`/`{` always wins as a field path (§3).
    if (this.isKw("rename") && !this.headIsField()) {
      this.advance();
      const s = this.check("STRING") ? this.advance() : null;
      if (!s) this.errK(this.peek(), "parse.renameNeedsLabel");
      return { kind: "rename", label: s?.value ?? "", source: this.spanFrom(tok) };
    }
    if (this.isKw("pin") && !this.headIsField()) {
      this.advance();
      const x = this.parseSignedNumber();
      this.consume("COMMA", "`pin` takes `x, y` coordinates");
      const y = this.parseSignedNumber();
      return { kind: "pin", x, y, source: this.spanFrom(tok) };
    }
    // A field path: `path { … }` (colon-less group) or `path: <expr>` (rewrite).
    if (this.check("IDENTIFIER")) {
      const path = this.parseQualifiedName();
      if (this.check("LBRACE")) {
        const members = this.parseStyleBody();
        return { kind: "group", path, members, source: this.spanFrom(tok) };
      }
      this.consume("COLON", "Expected ':' or '{' after a style field path");
      const value = this.parseExpression();
      return { kind: "rewrite", path, value, source: this.spanFrom(tok) };
    }
    return null;
  }

  // ===========================================================================
  // lens
  // ===========================================================================

  /** `lens { <name ["Title"]: <expr>>… }`. Current token is `lens`. */
  parseLens(): LensDecl {
    const start = this.advance(); // `lens`
    this.consume("LBRACE", "Expected '{' to open a lens body");
    const entries: LensEntry[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const e = this.parseLensEntry();
      if (e) entries.push(e);
      else {
        this.errK(this.peek(), "parse.lensEntryShape");
        this.advance();
      }
    }
    this.consume("RBRACE", "Expected '}' to close a lens body");
    return { entries, source: this.spanFrom(start) };
  }

  private parseLensEntry(): LensEntry | null {
    if (!this.check("IDENTIFIER")) return null;
    const nameTok = this.advance();
    let title: string | undefined;
    if (this.check("STRING")) title = this.advance().value;
    this.consume("COLON", "Expected ':' in a lens entry");
    const expr = this.parseExpression();
    return { name: nameTok.value, title, expr, source: this.spanFrom(nameTok) };
  }

  // ===========================================================================
  // use policy
  // ===========================================================================

  /** `use policy <Name> from <pkg> [{ except/escalate }]`. Current token is `use`. */
  parseUsePolicy(): UsePolicyStmt {
    const start = this.advance(); // USE token
    this.consumeKw("policy", "Expected `policy` after `use`");
    const nameTok = this.consume("IDENTIFIER", "Expected a policy name");
    this.consume("FROM", "Expected `from` in `use policy <Name> from <pkg>`");
    const fromStart = this.peek();
    const fromPackage = this.parseQualifiedName();
    const fromSource: SourceSpan = {
      start: fromStart.span.start,
      end: this.previous().span.end,
      file: fromStart.span.file,
    };
    const instanceBody: PolicyRule[] = [];
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (this.isKw("except")) instanceBody.push(this.parseExcept());
        else if (this.isKw("escalate")) instanceBody.push(this.parseEscalate());
        else {
          this.errK(this.peek(), "parse.usePolicyBodyOnly");
          this.advance();
        }
      }
      this.consume("RBRACE", "Expected '}' to close the instance body");
    }
    return {
      name: nameTok.value,
      nameSource: nameTok.span,
      fromPackage,
      fromSource,
      instanceBody,
      source: this.spanFrom(start),
    };
  }

  // ===========================================================================
  // view (v2) — ordered-clause view body (§3)
  // ===========================================================================

  /**
   * `view [#id] <Name> { <header> <clauses> }`. Header zone (description,
   * `key: value` fields, `knob` decls) first, then ordered clauses — the
   * process/policy header/body split. Instance form
   * `view [#id] <ParentView> <Name> { <bindings> <clauses> }` sets `instanceOf`.
   * The sole `view` parser — the main dispatch (`parser.ts`) delegates here.
   */
  parseView(exported = false): ViewDeclV2 {
    const start = this.consume("VIEW", "Expected `view`");
    const idTok = this.matchToken("STABLE_ID");
    const firstNameTok = this.consume("IDENTIFIER", "Expected a view name");

    // Instance form: a SECOND identifier before `{` names the instance; the
    // first is then the parent-view ref (`view HotSpots PaymentsHotSpots {`).
    let instanceOf: string | undefined;
    let nameTok = firstNameTok;
    if (this.check("IDENTIFIER")) {
      instanceOf = firstNameTok.value;
      nameTok = this.advance();
    }

    this.consume("LBRACE", "Expected '{' after the view name");

    const fields: FieldDecl[] = [];
    const knobs: KnobDecl[] = [];
    const bindings: KnobBinding[] = [];
    const clauses: ViewClause[] = [];
    let description: string | undefined;
    let sawClause = false;
    let firstRep: { kind: string; span?: SourceSpan } | undefined;
    let firstStory: SourceSpan | undefined;

    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;

      // --- Header zone: a description string ---------------------------------
      if (this.check("STRING")) {
        const s = this.advance();
        if (sawClause) {
          this.errK(s, "parse.viewDescBeforeClause");
          continue;
        }
        description = description === undefined ? s.value : `${description}\n${s.value}`;
        continue;
      }

      // --- Header zone: a `knob` declaration ---------------------------------
      if (this.isKnobHead()) {
        if (sawClause) this.errK(this.peek(), "parse.viewKnobBeforeClause");
        this.parseKnob(knobs);
        continue;
      }

      // --- Header field (or, on an instance, a knob binding) -----------------
      if (this.isFieldHead()) {
        if (sawClause) this.errK(this.peek(), "parse.viewHeaderBeforeClause");
        if (instanceOf) bindings.push(this.parseKnobBinding());
        else fields.push(this.parseField());
        continue;
      }

      // --- Legacy v0.9 clause spellings — targeted diagnostics, then recover -
      if (this.check("LAYOUT")) {
        const tok = this.advance();
        if (this.check("IDENTIFIER")) this.advance(); // the algorithm name
        this.errK(tok, "parse.layoutRemoved", { example: "`style X { pin … }`" });
        continue;
      }
      if (this.check("INCLUDE") || this.check("EXCLUDE")) {
        const tok = this.advance();
        const word = tok.type === "INCLUDE" ? "include" : "exclude";
        // Consume the legacy string list so the rest of the body still parses.
        if (this.check("STRING")) {
          this.advance();
          while (this.check("COMMA")) {
            this.advance();
            if (this.check("STRING")) this.advance();
          }
        }
        this.errK(tok, "parse.includeExcludeReserved", { word });
        continue;
      }

      // --- Ordered clauses ---------------------------------------------------
      const clause = this.parseViewClause();
      if (clause) {
        sawClause = true;
        if (VIEW_REPRESENTATIONS.has(clause.kind)) {
          if (firstRep) {
            this.errK(this.peek(), "parse.oneRepresentationClause", { kind: clause.kind, firstKind: firstRep.kind });
          } else {
            firstRep = { kind: clause.kind, span: clause.source };
          }
        }
        // One story per view, checked HERE and not only on instance merging.
        // `resolveViewInstance` diagnoses a second story appended by an
        // instance, but a plain view writing two in one body never reaches it —
        // the projection takes the last and the earlier chapters vanish with no
        // signal at all. Two ordered walks over one board is the thing a reader
        // cannot follow, whichever way the second one arrived.
        if (clause.kind === "story") {
          if (firstStory) this.errK(this.peek(), "parse.oneStoryClause");
          else firstStory = clause.source;
        }
        clauses.push(clause);
        continue;
      }

      // Unrecognised member — recover.
      this.errK(this.peek(), "parse.unexpectedInViewBody", { tok: this.peek().value || this.peek().type });
      this.advance();
    }

    this.consume("RBRACE", "Expected '}' to close the view body");

    // flow/on mutual constraint (§3): a flow view carries the process's own
    // plane — `on <plane>` has nothing to set. Diagnosed on the `on` clause.
    if (clauses.some((c) => c.kind === "flow")) {
      for (const c of clauses) {
        if (c.kind === "on") {
          this.errKAt(c.source, "parse.onInFlowView");
        }
      }
    }

    return {
      ...(idTok ? { id: stableId(idTok.value.slice(1)) as StableId, idSource: idTok.span } : {}),
      name: nameTok.value,
      nameSource: nameTok.span,
      ...(description !== undefined ? { description } : {}),
      header: { fields, knobs },
      clauses,
      ...(instanceOf ? { instanceOf, bindings } : {}),
      ...(exported ? { exported: true } : {}),
      source: this.spanFrom(start),
    };
  }

  /** Dispatch one ordered view clause; `null` = not a clause head. */
  private parseViewClause(): ViewClause | null {
    const start = this.peek();
    if (this.isKw("show")) {
      this.advance();
      return { kind: "show", selector: this.parseSelector(), source: this.spanFrom(start) };
    }
    if (this.isKw("hide")) {
      this.advance();
      return { kind: "hide", selector: this.parseSelector(), source: this.spanFrom(start) };
    }
    if (this.check("FOCUS")) return this.parseFocusClause();
    if (this.check("GROUP")) return this.parseGroupByClause();
    if (this.check("ON")) return this.parseOnClause();
    if (this.isKw("style") && !this.headIsField()) return this.parseStyleClause();
    if (this.isKw("flow")) return this.parseFlowClause();
    if (this.isKw("table") && this.peekN(1)?.type === "LBRACE") return this.parseTableClause();
    if (this.isKw("matrix")) return this.parseMatrixClause();
    if (this.isKw("grid")) return this.parseGridClause();
    if (this.isKw("index")) return this.parseIndexClause();
    // `story {` — the `{` lookahead is the `table`/`lens` guard: a view header
    // field spelled `story: …` must stay a field.
    if (this.isKw("story") && this.peekN(1)?.type === "LBRACE") return this.parseStoryClause();
    // `at now` / `at v3` / `at now..v3`. Guarded by `headIsField()` exactly as
    // `style` is — a view header field named `at:` must stay a field.
    if (this.isKw("at") && !this.headIsField()) return this.parseAtClause();
    if (this.isKw("lens") && this.peekN(1)?.type === "LBRACE") {
      const d = this.parseLens();
      return { kind: "lens", entries: d.entries, source: d.source };
    }
    return null;
  }

  /** `at <point>` or `at <point>..<point>` (the two-point roadmap delta). */
  private parseAtClause(): ViewClause {
    const start = this.advance(); // `at`
    const at = this.parseScrubPoint();
    let compare: ScrubPoint | undefined;
    // `..` lexes as two DOTs; the range form is the delta.
    if (this.check("DOT") && this.peekN(1)?.type === "DOT") {
      this.advance();
      this.advance();
      compare = this.parseScrubPoint();
    }
    return { kind: "at", at, compare, source: this.spanFrom(start) };
  }

  private parseScrubPoint(): ScrubPoint {
    if (this.check("STRING")) return { kind: "marker", text: this.advance().value };
    const tok = this.advance();
    if (tok.value === "now") return { kind: "now" };
    // Any bare token is a marker. A dotted chain of numbers glues back into one
    // version marker (`at 1.5.2`), exactly as a decoration argument does — the
    // two spell the same thing and must lex the same way. A date stays quoted so
    // it cannot collide with number lexing or the internal-hyphen-join rule.
    let text = tok.value;
    while (this.check("DOT") && this.peekN(1)?.type === "NUMBER") {
      this.advance();
      text += "." + this.advance().value;
    }
    return { kind: "marker", text };
  }

  /** `focus <selector>`; legacy `focus <key>: <value>` fires a targeted error. */
  private parseFocusClause(): ViewClause {
    const start = this.advance(); // FOCUS
    // Legacy v0.9 aspect-focus: `focus domain: Payments` (bare KEY : VALUE).
    if (this.check("IDENTIFIER") && this.peekN(1)?.type === "COLON") {
      const keyTok = this.advance();
      this.advance(); // `:`
      const valTok = this.peek();
      this.parseFieldValue(); // consume the value for recovery
      const val = valTok.value;
      this.errK(keyTok, "parse.focusAspectSyntax", { key: keyTok.value, val });
      return { kind: "focus", selector: { kind: "ref", path: [keyTok.value], source: keyTok.span }, source: this.spanFrom(start) };
    }
    return { kind: "focus", selector: this.parseSelector(), source: this.spanFrom(start) };
  }

  /** `group by <getter>` / `group by in <selector>`; legacy bare
   *  `group by team` fires a targeted error. */
  private parseGroupByClause(): ViewClause {
    const start = this.advance(); // GROUP
    this.consume("BY", "Expected `by` after `group`");
    // Containment grouping: the selector after `in` names the CONTAINERS, so a
    // whole selector (`in (A or B)`, `in space`) is legal, not just a ref.
    if (this.check("IN")) {
      this.advance();
      return { kind: "groupBy", scope: this.parseSelector(), source: this.spanFrom(start) };
    }
    if (this.check("AT") || this.check("ATAT")) {
      return { kind: "groupBy", getter: this.parseGetter(), source: this.spanFrom(start) };
    }
    // Legacy v0.9 bare getter: `group by team` (no sigil).
    if (this.check("IDENTIFIER")) {
      const idTok = this.advance();
      this.errK(idTok, "parse.groupByGetterSigiled", { key: idTok.value });
      return {
        kind: "groupBy",
        getter: { kind: "getter", sigil: "@@", path: [idTok.value], source: idTok.span },
        source: this.spanFrom(start),
      };
    }
    this.errK(this.peek(), "parse.groupByNeedsGetter");
    return {
      kind: "groupBy",
      getter: { kind: "getter", sigil: "@@", path: [""], source: this.peek().span },
      source: this.spanFrom(start),
    };
  }

  /** `on <plane-list>` — comma-separated plane names on one logical line. */
  private parseOnClause(): ViewClause {
    const start = this.advance(); // ON
    const planes: string[] = [];
    for (;;) {
      if (!this.check("IDENTIFIER")) break;
      planes.push(this.parseQualifiedName().join("."));
      if (!this.check("COMMA")) break;
      this.advance(); // `,`
    }
    if (planes.length === 0) this.errK(this.peek(), "parse.onNeedsPlane");
    return { kind: "on", planes, source: this.spanFrom(start) };
  }

  /** `style <selector> { <members> }` — subject reuses the selector grammar. */
  private parseStyleClause(): ViewClause {
    const start = this.advance(); // `style`
    const subject = this.parseSelector();
    const members = this.parseStyleBody();
    return { kind: "style", subject, members, source: this.spanFrom(start) };
  }

  /** `flow <ProcessRef | $knob> [sequence | bpmn] [{ <lane/pool> }]`. */
  private parseFlowClause(): ViewClause {
    const start = this.advance(); // `flow`
    let ref: FlowRef;
    if (this.check("DOLLAR")) {
      this.advance();
      const knob = this.consume("IDENTIFIER", "Expected a knob name after '$'").value;
      ref = { kind: "knob", name: knob };
    } else {
      ref = { kind: "ref", path: this.parseQualifiedName() };
    }
    let mode: "sequence" | "bpmn" | undefined;
    if (this.isKw("sequence") || this.isKw("bpmn")) {
      mode = this.advance().value as "sequence" | "bpmn";
    } else if (this.isKw("swimlane")) {
      // Retired v0.10.2 token — plain flow already IS the swimlane walkthrough.
      this.errK(this.advance(), "parse.swimlaneRetired");
    }
    let body: readonly FlowBodyClause[] | undefined;
    if (this.check("LBRACE")) {
      const bodyStart = this.peek();
      body = this.parseFlowBody();
      if (mode !== "bpmn") {
        // Inert body: attached for tooling, diagnosed for the author.
        this.errK(bodyStart, "parse.flowBodyBpmnOnly", { example: "`flow X bpmn { … }`" });
      }
    }
    return {
      kind: "flow",
      ref,
      ...(mode ? { mode } : {}),
      ...(body ? { body } : {}),
      source: this.spanFrom(start),
    };
  }

  /** `{ lane … | pool … }` — the flow-body lane/pool configuration. */
  private parseFlowBody(): readonly FlowBodyClause[] {
    this.consume("LBRACE", "Expected '{' to open a flow body");
    const members: FlowBodyClause[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const start = this.peek();
      if (this.isKw("lane")) {
        this.advance();
        if (this.check("BY")) {
          const getter = this.parseFlowBodyGetter("lane by");
          members.push({ kind: "lane-by", getter, source: this.spanFrom(start) });
        } else if (this.check("STRING")) {
          const titleTok = this.advance();
          this.consume("COLON", 'A titled lane takes a selector: `lane "Title": <selector>`');
          if (!this.flowBodySelectorFollows(titleTok, "lane")) continue;
          const selector = this.parseSelector();
          members.push({
            kind: "lane-merge",
            title: titleTok.value,
            selector,
            source: this.spanFrom(start),
          });
        } else {
          const selector = this.parseSelector();
          members.push({ kind: "lane-expand", selector, source: this.spanFrom(start) });
        }
        continue;
      }
      if (this.isKw("pool")) {
        this.advance();
        if (this.check("BY")) {
          const getter = this.parseFlowBodyGetter("pool by");
          members.push({ kind: "pool-by", getter, source: this.spanFrom(start) });
        } else if (this.check("STRING")) {
          const titleTok = this.advance();
          this.consume("COLON", 'A titled pool takes a query: `pool "Title": <selector>`');
          if (!this.flowBodySelectorFollows(titleTok, "pool")) continue;
          const selector = this.parseSelector();
          members.push({
            kind: "pool-merge",
            title: titleTok.value,
            selector,
            source: this.spanFrom(start),
          });
        } else {
          // Parse a FULL selector first, then shape-check (round 2 finding 13):
          // recovering mid-selector on `pool service and in X` reads terribly.
          const selector = this.parseSelector();
          if (selector.kind === "ref") {
            members.push({
              kind: "pool-ref",
              ref: { kind: "ref", path: selector.path },
              source: this.spanFrom(start),
            });
          } else if (selector.kind === "knob") {
            members.push({
              kind: "pool-ref",
              ref: { kind: "knob", name: selector.name },
              source: this.spanFrom(start),
            });
          } else {
            this.errK(start, "parse.untitledPoolElementRef");
            members.push({
              kind: "pool-merge",
              title: "",
              selector,
              source: this.spanFrom(start),
            });
          }
        }
        continue;
      }
      if (this.isKw("replicate")) {
        this.advance();
        const sel = this.parseSelector();
        if (sel.kind === "aspect") {
          members.push({ kind: "replicate", key: sel.path.join("."), source: this.spanFrom(start) });
        } else {
          this.errK(start, "parse.replicateAspectPlane");
        }
        continue;
      }
      if (this.isKw("engine")) {
        this.advance();
        const sel = this.parseSelector();
        if (sel.kind === "aspect") {
          members.push({ kind: "engine", key: sel.path.join("."), source: this.spanFrom(start) });
        } else {
          this.errK(start, "parse.engineAspectPlane");
        }
        continue;
      }
      this.errK(this.peek(), "parse.flowBodyEntries");
      this.advance();
    }
    this.consume("RBRACE", "Expected '}' to close the flow body");
    return members;
  }

  /** A titled lane/pool's selector must START on the title's logical line —
   *  the `rename`-directive precedent. Without the gate, a dangling `:`
   *  before `}` makes selector recovery eat the body's close (fleet finding
   *  6), and a next-line member head gets stolen as the selector (finding 7).
   *  Returns false (after a targeted error) when no selector follows. */
  private flowBodySelectorFollows(titleTok: Token, head: "lane" | "pool"): boolean {
    const next = this.peek();
    if (next.type !== "RBRACE" && next.span.start.line === titleTok.span.start.line) return true;
    this.errK(next, "parse.titledLanePoolSameLine", { head });
    return false;
  }

  /** The `by <getter>` tail shared by `lane by` / `pool by` (group-by shape). */
  private parseFlowBodyGetter(head: string): ExprGetter {
    this.advance(); // BY
    if (this.check("AT") || this.check("ATAT")) return this.parseGetter();
    this.errK(this.peek(), "parse.laneOrPoolByGetter", { head });
    return { kind: "getter", sigil: "@@", path: [""], source: this.peek().span };
  }

  /** `table { <columns> [sort by <getter>] }`. */
  private parseTableClause(): ViewClause {
    const start = this.advance(); // `table`
    this.consume("LBRACE", "Expected '{' to open a table body");
    const columns: TableColumn[] = [];
    let sortBy: ExprGetter | undefined;
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      if (this.isKw("sort")) {
        this.advance();
        this.consume("BY", "Expected `by` after `sort`");
        if (this.check("AT") || this.check("ATAT")) sortBy = this.parseGetter();
        else this.errK(this.peek(), "parse.sortByGetter");
        continue;
      }
      if (this.isKw("column")) {
        columns.push(this.parseTableColumn());
        continue;
      }
      this.errK(this.peek(), "parse.tableBodyEntries");
      this.advance();
    }
    this.consume("RBRACE", "Expected '}' to close the table body");
    return { kind: "table", columns, ...(sortBy ? { sortBy } : {}), source: this.spanFrom(start) };
  }

  /**
   * `story { chapter "Title" { "note" <selectors> } … }` — a guided walk.
   *
   * Not a representation: a story rides on whatever the view already draws, so
   * it never enters the one-representation check.
   */
  private parseStoryClause(): ViewClause {
    const start = this.advance(); // `story`
    this.consume("LBRACE", "Expected '{' to open a story body");
    const chapters: StoryChapter[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      if (this.isKw("chapter")) {
        const chapter = this.parseStoryChapter();
        // Over-cap chapters are diagnosed AND dropped. Diagnosing alone would
        // leave every downstream consumer re-checking a cap the grammar
        // already stated; dropping makes "a parsed story has at most five
        // chapters" true by construction.
        if (chapters.length >= STORY_CHAPTER_CAP) {
          this.errKAt(chapter.source, "parse.storyChapterCap", { cap: STORY_CHAPTER_CAP });
        } else {
          chapters.push(chapter);
        }
        continue;
      }
      this.errK(this.peek(), "parse.storyBodyEntries");
      this.advance();
    }
    this.consume("RBRACE", "Expected '}' to close the story body");
    if (chapters.length === 0) this.errKAt(this.spanFrom(start), "parse.storyEmpty");
    return { kind: "story", chapters, source: this.spanFrom(start) };
  }

  /** `chapter "Title" { ["note"] <selector>[, <selector>…] }` — one story beat set. */
  private parseStoryChapter(): StoryChapter {
    const start = this.advance(); // `chapter`
    const titleTok = this.consume("STRING", "Expected a chapter title string");
    this.consume("LBRACE", "Expected '{' to open a chapter body");
    let note: string | undefined;
    const selectors: Selector[] = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      // Node entries separate by comma OR newline, like every other list body.
      if (this.check("COMMA")) {
        this.advance();
        continue;
      }
      if (this.check("RBRACE") || this.isAtEnd()) break;
      // A LEADING string is the note — the same law a view or module
      // description obeys. One placed after the nodes is a mistake worth
      // naming: it reads like a per-node caption, which a chapter has no slot
      // for, and silently appending it to the note would hide that.
      if (this.check("STRING")) {
        const s = this.advance();
        if (selectors.length > 0) {
          this.errK(s, "parse.storyNoteBeforeNodes");
          continue;
        }
        note = note === undefined ? s.value : `${note}\n${s.value}`;
        continue;
      }
      const before = this.pos;
      selectors.push(this.parseSelector());
      // A selector production that consumed nothing would spin here forever.
      // Every other body loop in this parser advances on its own error path;
      // this one delegates, so the guard is explicit.
      if (this.pos === before) {
        this.errK(this.peek(), "parse.storyChapterEntries");
        this.advance();
        selectors.pop();
      }
    }
    this.consume("RBRACE", "Expected '}' to close the chapter body");
    if (selectors.length === 0) {
      this.errKAt(this.spanFrom(start), "parse.storyChapterEmpty", { title: titleTok.value });
    }
    return {
      title: titleTok.value,
      ...(note !== undefined ? { note } : {}),
      selectors,
      source: this.spanFrom(start),
      titleSource: titleTok.span,
    };
  }

  /** `index [{ [sort by <getter>] [columns <n>] }]` — the catalog representation.
   *  The body is OPTIONAL (like `matrix` / `grid`): a bare `index` is the default
   *  catalog, sorted by `@name` at an auto column count. */
  private parseIndexClause(): ViewClause {
    const start = this.advance(); // `index`
    let sortBy: ExprGetter | undefined;
    let columns: number | undefined;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (this.isKw("sort")) {
          this.advance();
          this.consume("BY", "Expected `by` after `sort`");
          if (this.check("AT") || this.check("ATAT")) sortBy = this.parseGetter();
          else this.errK(this.peek(), "parse.sortByGetter");
          continue;
        }
        if (this.isKw("columns")) {
          this.advance();
          if (this.check("NUMBER")) columns = Math.max(1, Math.floor(Number(this.advance().value)));
          else this.errK(this.peek(), "parse.indexColumnsNumber");
          continue;
        }
        this.errK(this.peek(), "parse.indexBodyEntries");
        this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close the index body");
    }
    return {
      kind: "index",
      ...(sortBy ? { sortBy } : {}),
      ...(columns !== undefined ? { columns } : {}),
      source: this.spanFrom(start),
    };
  }

  private parseTableColumn(): TableColumn {
    const start = this.advance(); // `column`
    // Getter form: `column @getter ["Title"]` — editable.
    if (this.check("AT") || this.check("ATAT")) {
      const getter = this.parseGetter();
      let title: string | undefined;
      if (this.check("STRING")) title = this.advance().value;
      return { kind: "getter", getter, ...(title !== undefined ? { title } : {}), source: this.spanFrom(start) };
    }
    // Calc form: `column "Title": <expr>` — read-only.
    if (this.check("STRING")) {
      const titleTok = this.advance();
      this.consume("COLON", 'A calculated column is `column "Title": <expr>`');
      const expr = this.parseExpression();
      return { kind: "calc", title: titleTok.value, expr, source: this.spanFrom(start) };
    }
    this.errK(this.peek(), "parse.columnShape");
    return { kind: "calc", title: "", expr: { kind: "literal", value: { kind: "string", text: "", source: start.span } }, source: this.spanFrom(start) };
  }

  /**
   * `matrix { [axis <getter>] [rows <getter>] [cols <getter>] [order by
   * <strategy>] [cluster] }` — the DSM representation. A canonical DSM is SQUARE
   * (one shared axis, meaningful diagonal); `axis` groups that shared axis,
   * `rows`/`cols` diverge only for a rectangular coupling matrix. `order by`
   * takes an analysis keyword (`cluster`/`layer`) or a getter; bare `cluster`
   * boxes SCC cycles. `order`/`cluster` alongside `rows`/`cols` is a square-DSM
   * error (`parse.matrixClusterRectangular`).
   */
  private parseMatrixClause(): ViewClause {
    const start = this.advance(); // `matrix`
    let axis: ExprGetter | undefined;
    let rows: ExprGetter | undefined;
    let cols: ExprGetter | undefined;
    let order: MatrixOrder | undefined;
    let cluster = false;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (this.isKw("axis")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) axis = this.parseGetter();
          else this.errK(this.peek(), "parse.axisGetter");
          continue;
        }
        if (this.isKw("rows")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) rows = this.parseGetter();
          else this.errK(this.peek(), "parse.rowsGetter");
          continue;
        }
        if (this.isKw("cols")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) cols = this.parseGetter();
          else this.errK(this.peek(), "parse.colsGetter");
          continue;
        }
        if (this.isKw("order")) {
          const oStart = this.advance();
          this.consume("BY", "Expected `by` after `order`");
          if (this.isKw("cluster")) {
            this.advance();
            order = { strategy: { kind: "cluster" }, source: this.spanFrom(oStart) };
          } else if (this.isKw("layer")) {
            this.advance();
            order = { strategy: { kind: "layer" }, source: this.spanFrom(oStart) };
          } else if (this.check("AT") || this.check("ATAT")) {
            order = { strategy: { kind: "getter", getter: this.parseGetter() }, source: this.spanFrom(oStart) };
          } else {
            this.errK(this.peek(), "parse.orderStrategy");
          }
          continue;
        }
        if (this.isKw("cluster")) {
          this.advance();
          cluster = true;
          continue;
        }
        this.errK(this.peek(), "parse.matrixBodyEntries");
        this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close the matrix body");
    }
    // `axis` is the grouped SQUARE form; `rows`/`cols` the rectangular one. They
    // are mutually exclusive — coexisting would silently drop `axis`.
    if (axis && (rows || cols)) {
      this.errK(start, "parse.matrixAxisWithRowsCols");
    }
    // A rectangular matrix (divergent rows/cols) has no meaningful diagonal, so
    // clustering / topological ordering are rejected — steer to a square `axis`.
    if ((rows || cols) && (cluster || order)) {
      this.errK(start, "parse.matrixClusterRectangular");
    }
    return {
      kind: "matrix",
      ...(axis ? { axis } : {}),
      ...(rows ? { rows } : {}),
      ...(cols ? { cols } : {}),
      ...(order ? { order } : {}),
      ...(cluster ? { cluster } : {}),
      source: this.spanFrom(start),
    };
  }

  /**
   * `grid { [rows <getter>] [cols <getter>] [color <getter>] }` — the cross-tab
   * representation. `rows`/`cols` group the shown modules by a getter; each cell
   * lists the modules at that (row, col) intersection. The optional `color`
   * getter tints members by a third field. Body clauses are order-free.
   */
  private parseGridClause(): ViewClause {
    const start = this.advance(); // `grid`
    let rows: ExprGetter | undefined;
    let cols: ExprGetter | undefined;
    let color: ExprGetter | undefined;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (this.isKw("rows")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) rows = this.parseGetter();
          else this.errK(this.peek(), "parse.rowsGetter");
          continue;
        }
        if (this.isKw("cols")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) cols = this.parseGetter();
          else this.errK(this.peek(), "parse.colsGetter");
          continue;
        }
        if (this.isKw("color")) {
          this.advance();
          if (this.check("AT") || this.check("ATAT")) color = this.parseGetter();
          else this.errK(this.peek(), "parse.colorGetter");
          continue;
        }
        this.errK(this.peek(), "parse.gridBodyEntries");
        this.advance();
      }
      this.consume("RBRACE", "Expected '}' to close the grid body");
    }
    return {
      kind: "grid",
      ...(rows ? { rows } : {}),
      ...(cols ? { cols } : {}),
      ...(color ? { color } : {}),
      source: this.spanFrom(start),
    };
  }

  // ---- Knobs (declaration grammar only; S18 owns evaluation/UI) -------------

  /** True at a `knob` head that opens a declaration (not a field named `knob`). */
  private isKnobHead(): boolean {
    if (!this.isKw("knob")) return false;
    const next = this.peekN(1)?.type;
    return next !== "COLON" && next !== "DOT"; // head + `:`/`.` = a field literally named `knob`
  }

  /** Parse one `knob` head (block form pushes several) into `out`. */
  private parseKnob(out: KnobDecl[]): void {
    const start = this.advance(); // `knob`
    // Block form: `knob { <name> one of: …; <name> any of: … }`.
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        this.skipSeparators();
        if (this.check("RBRACE") || this.isAtEnd()) break;
        const e = this.parseKnobEnumEntry();
        if (e) out.push(e);
        else {
          this.errK(this.peek(), "parse.knobBlockEntryShape");
          this.advance();
        }
      }
      this.consume("RBRACE", "Expected '}' to close the knob block");
      return;
    }

    // `process`/`subprocess` lex as keyword tokens but are legal kind names
    // in the knob kind slot (`knob process target` — "any type name",
    // VIEWS.md §10; a flow-target knob is naturally process-kind).
    const nameTok =
      this.check("PROCESS") || this.check("SUBPROCESS")
        ? this.advance()
        : this.consume("IDENTIFIER", "Expected a knob name or kind");

    // Literal-default: `knob <name>: <literal> [{ min/max/step }]`.
    if (this.check("COLON")) {
      this.advance();
      const def = this.parseFieldValue();
      const restrictions = this.check("LBRACE") ? this.parseKnobRestrictions() : {};
      out.push({
        kind: "number",
        name: nameTok.value,
        default: def,
        ...(restrictions.min !== undefined ? { min: restrictions.min } : {}),
        ...(restrictions.max !== undefined ? { max: restrictions.max } : {}),
        ...(restrictions.step !== undefined ? { step: restrictions.step } : {}),
        nameSource: nameTok.span,
        source: this.spanFrom(start),
      });
      return;
    }

    // From-enumeration: `knob <name> from @@key`.
    if (this.check("FROM")) {
      this.advance();
      if (!(this.check("AT") || this.check("ATAT"))) {
        this.errK(this.peek(), "parse.knobFromSigiled");
      }
      const getter = this.parseGetter();
      out.push({ kind: "from", name: nameTok.value, getter, nameSource: nameTok.span, source: this.spanFrom(start) });
      return;
    }

    // Kind-led element picker: `knob <type> <name>`.
    if (this.check("IDENTIFIER")) {
      const picked = this.advance();
      out.push({ kind: "element", elementType: nameTok.value, name: picked.value, nameSource: picked.span, source: this.spanFrom(start) });
      return;
    }

    this.errK(this.peek(), "parse.knobShape", { block: "`{ … }`" });
  }

  private parseKnobEnumEntry(): KnobDecl | null {
    if (!this.check("IDENTIFIER")) return null;
    const nameTok = this.advance();
    let select: "one" | "any";
    if (this.check("ONE")) {
      this.advance();
      select = "one";
    } else if (this.isKw("any")) {
      this.advance();
      select = "any";
    } else {
      this.errK(this.peek(), "parse.knobBlockOneOrAny");
      return null;
    }
    this.consumeKw("of", "Expected `of` after `one`/`any`");
    this.consume("COLON", "Expected ':' before the knob value list");
    const values: Value[] = [this.parseAtomicValue()];
    while (this.check("COMMA")) {
      this.advance();
      values.push(this.parseAtomicValue());
    }
    return { kind: "enum", name: nameTok.value, select, values, nameSource: nameTok.span, source: this.spanFrom(nameTok) };
  }

  /** `{ min: N; max: N; step: N }` — the frozen restriction vocabulary (OQ8). */
  private parseKnobRestrictions(): { min?: number; max?: number; step?: number } {
    this.consume("LBRACE", "Expected '{' to open knob restrictions");
    const out: { min?: number; max?: number; step?: number } = {};
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.skipSeparators();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const keyTok = this.consume("IDENTIFIER", "A knob restriction is `min` / `max` / `step`");
      this.consume("COLON", "Expected ':' in a knob restriction");
      const n = this.parseSignedNumber();
      if (keyTok.value === "min") out.min = n;
      else if (keyTok.value === "max") out.max = n;
      else if (keyTok.value === "step") out.step = n;
      else this.errK(keyTok, "parse.knobRestrictionVocab");
    }
    this.consume("RBRACE", "Expected '}' to close knob restrictions");
    return out;
  }

  /** A view-instance binding: `<name>: <value>` (kind-determined value slot).
   *  An identifier RHS may be dotted (`target: Payments.Api` → a `ref` Value)
   *  — the qualified spelling an ELEMENT knob needs when the bare suffix is
   *  ambiguous (`resolveRef` on a short-name tie returns nothing). */
  private parseKnobBinding(): KnobBinding {
    const start = this.peek();
    const nameTok = this.consume("IDENTIFIER", "Expected a knob name in a binding");
    this.consume("COLON", "Expected ':' in a knob binding");
    let value = this.parseFieldValue();
    if (value.kind === "identifier" && this.check("DOT")) {
      const path: string[] = [value.text];
      let end = value.source ?? nameTok.span;
      while (this.check("DOT")) {
        this.advance();
        const seg = this.consume("IDENTIFIER", "Expected a path segment after '.' in a knob binding");
        path.push(seg.value);
        end = seg.span;
      }
      value = {
        kind: "ref",
        path,
        source: value.source
          ? { start: value.source.start, end: end.end, file: value.source.file }
          : undefined,
      };
    }
    return { name: nameTok.value, value, nameSource: nameTok.span, source: this.spanFrom(start) };
  }

  /**
   * Emit a catalogued diagnostic at `token` — derives the English `message`
   * from the `parse.*` catalog key and rides `code`/`msgKey`/`params` for
   * per-locale rendering. The declaration-parser counterpart to the lexer's
   * `error` and the resolver's `makeDiag`.
   */
  private errK(token: Token, msgKey: string, params?: Record<string, unknown>): void {
    const message = renderDiagnostic(msgKey, params, "en") ?? msgKey;
    (this.errors as EnrichedParseError[]).push({
      message,
      span: token.span,
      code: "PARSE_ERROR",
      msgKey,
      ...(params ? { params } : {}),
    });
  }

  /** `errK` bound to an arbitrary span (post-parse clause checks). */
  private errKAt(span: SourceSpan | undefined, msgKey: string, params?: Record<string, unknown>): void {
    const message = renderDiagnostic(msgKey, params, "en") ?? msgKey;
    (this.errors as EnrichedParseError[]).push({
      message,
      span,
      code: "PARSE_ERROR",
      msgKey,
      ...(params ? { params } : {}),
    });
  }

  // ===========================================================================
  // Shared field parsing
  // ===========================================================================

  /** True when the current head token is followed by `:` or `.` (→ a field). */
  private headIsField(): boolean {
    const next = this.peekN(1)?.type;
    return next === "COLON" || next === "DOT";
  }

  private parseSignedNumber(): number {
    const neg = this.check("MINUS");
    if (neg) this.advance();
    const tok = this.consume("NUMBER", "Expected a number");
    const n = Number(tok.value);
    return neg ? -n : n;
  }

  /** A member head that is a field: `path` immediately followed by `:` or `.`. */
  private isFieldHead(): boolean {
    if (!this.check("IDENTIFIER")) return false;
    const next = this.peekN(1)?.type;
    return next === "COLON" || next === "DOT";
  }

  /** `path: value` — a scalar field (strings / idents / numbers incl. signed). */
  private parseField(): FieldDecl {
    const start = this.peek();
    const path = this.parseQualifiedName();
    this.consume("COLON", "Expected ':' in a field");
    const value = this.parseFieldValue();
    return {
      path,
      value,
      source: this.spanFrom(start),
      pathSource: start.span,
    };
  }

  private parseFieldValue(): Value {
    const tok = this.peek();
    if (tok.type === "MINUS" && this.peekN(1)?.type === "NUMBER") {
      this.advance();
      const num = this.advance();
      return {
        kind: "number",
        value: -Number(num.value),
        source: { start: tok.span.start, end: num.span.end, file: tok.span.file },
      };
    }
    if (tok.type === "TRUE" || tok.type === "FALSE") {
      this.advance();
      return { kind: "boolean", value: tok.type === "TRUE", source: tok.span };
    }
    return this.parseAtomicValue();
  }

  /** Consume `;` separators between members (newlines are trivia, not tokens). */
  private skipSeparators(): void {
    while (this.check("SEMICOLON")) this.advance();
  }
}

export interface QueryParseResult {
  readonly decl: QueryDecl;
  readonly errors: readonly SelectorParseError[];
}

export interface PolicyParseResult {
  readonly decl: PolicyDecl;
  readonly errors: readonly SelectorParseError[];
}

function makeParser(src: string, file?: string): { p: V010DeclParser; tokenCount: number; lexErrors: SelectorParseError[] } {
  const lex = tokenize(src, file);
  const p = new V010DeclParser(lex.tokens);
  p.rawSource = src;
  return { p, tokenCount: lex.tokens.length, lexErrors: lex.errors.map((e) => ({ ...e })) };
}

/** Tokenise `src` and parse a single `query` declaration to EOF (fixtures). */
export function parseQuerySource(src: string, file?: string): QueryParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseQuery();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after query" });
  return { decl, errors };
}

/** Tokenise `src` and parse a single `policy` declaration to EOF (fixtures). */
export function parsePolicySource(src: string, file?: string): PolicyParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parsePolicy();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after policy" });
  return { decl, errors };
}

export interface TestParseResult {
  readonly decl: TestDecl;
  readonly errors: readonly SelectorParseError[];
}

/** Tokenise `src` and parse a single `test` declaration to EOF (fixtures). */
export function parseTestSource(src: string, file?: string): TestParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseTest();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after test" });
  return { decl, errors };
}

export interface StyleBundleParseResult {
  readonly decl: StyleBundleDecl;
  readonly errors: readonly SelectorParseError[];
}

export interface LensParseResult {
  readonly decl: LensDecl;
  readonly errors: readonly SelectorParseError[];
}

/** Tokenise `src` and parse a single `style bundle` declaration to EOF (fixtures). */
export function parseStyleBundleSource(src: string, file?: string): StyleBundleParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseStyleBundle();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after style bundle" });
  return { decl, errors };
}

/** Tokenise `src` and parse a single `lens` declaration to EOF (fixtures). */
export function parseLensSource(src: string, file?: string): LensParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseLens();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after lens" });
  return { decl, errors };
}

export interface UsePolicyParseResult {
  readonly decl: UsePolicyStmt;
  readonly errors: readonly SelectorParseError[];
}

/** Tokenise `src` and parse a single `use policy` statement to EOF (fixtures). */
export function parseUsePolicySource(src: string, file?: string): UsePolicyParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseUsePolicy();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after use policy" });
  return { decl, errors };
}

export interface ViewParseResult {
  readonly decl: ViewDeclV2;
  readonly errors: readonly SelectorParseError[];
}

/** Tokenise `src` and parse a single v0.10 `view` declaration to EOF (fixtures). */
export function parseViewSource(src: string, file?: string): ViewParseResult {
  const { p, tokenCount, lexErrors } = makeParser(src, file);
  const decl = p.parseView();
  const errors = [...lexErrors, ...p.errors];
  if (p.pos < tokenCount - 1) errors.push({ message: "Unexpected trailing input after view" });
  return { decl, errors };
}
